import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { X, Clock, MapPin, Users, Save } from 'lucide-react';

export default function BulkEditModal({ isOpen, selectedRecurrences, onClose, onSuccess }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formData, setFormData] = useState({
        start_time: '',
        end_time: '',
        location: '',
        max_participants: '',
        notes: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (error) setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (new Date(formData.start_time) >= new Date(formData.end_time)) {
            setError(t('gym.courses.bulkEditModal.messages.invalidTimeRange'));
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const api = getAuthenticatedApi();
            let successCount = 0;
            let errorCount = 0;
            
            // Prepara i dati per l'aggiornamento bulk
            const updateData = {};
            
            // Aggiungi solo i campi che sono stati modificati
            if (formData.start_time) {
                updateData.start_time = formData.start_time; // Invia solo HH:MM, l'API aggiunge :00
            }
            if (formData.end_time) {
                updateData.end_time = formData.end_time;     // Invia solo HH:MM, l'API aggiunge :00
            }
            if (formData.location !== '') {
                updateData.location = formData.location || null;
            }
            if (formData.max_participants !== '') {
                updateData.max_participants = formData.max_participants ? parseInt(formData.max_participants) : null;
            }
            if (formData.notes !== '') {
                updateData.notes = formData.notes || null;
            }
            
            // Solo se ci sono dati da aggiornare
            if (Object.keys(updateData).length > 0) {
                // Aggiungi gli ID delle sessioni ai dati
                updateData.session_ids = selectedRecurrences;
                
                // Chiama l'endpoint bulk
                const response = await api.put('/gym_courses.php?action=bulk_update_sessions', updateData);
                
                if (response.data.success) {
                    successCount = response.data.updated_count || selectedRecurrences.length;
                    errorCount = selectedRecurrences.length - successCount;
                } else {
                    errorCount = selectedRecurrences.length;
                }
            }
            
            if (successCount > 0) {
                toast.success(t('gym.courses.bulkEditModal.messages.success', { 
                    successCount, 
                    errorCount: errorCount > 0 ? `\n${t('gym.courses.bulkEditModal.messages.error', { errorCount })}` : '' 
                }));
                onSuccess();
            } else {
                throw new Error(t('gym.courses.bulkEditModal.messages.noUpdates'));
            }
            
        } catch (err) {
            console.error('Errore aggiornamento bulk:', err);
            setError(err.message || t('gym.courses.bulkEditModal.messages.updateError'));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {t('gym.courses.bulkEditModal.title')}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
                            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                        </div>
                    )}

                    <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg">
                        <p className="text-blue-700 dark:text-blue-400 text-sm">
                            {t('gym.courses.bulkEditModal.info', { count: selectedRecurrences.length }).split('<strong>').map((part, index) => {
                                if (index === 0) return part;
                                const [before, after] = part.split('</strong>');
                                return (
                                    <span key={index}>
                                        <strong>{before}</strong>
                                        {after}
                                    </span>
                                );
                            })}
                        </p>
                    </div>

                    {/* Orari */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <Clock className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.bulkEditModal.fields.newStartTimeLabel')}
                            </label>
                            <input
                                type="time"
                                name="start_time"
                                value={formData.start_time}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                placeholder={t('gym.courses.bulkEditModal.fields.placeholder')}
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <Clock className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.bulkEditModal.fields.newEndTimeLabel')}
                            </label>
                            <input
                                type="time"
                                name="end_time"
                                value={formData.end_time}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                placeholder={t('gym.courses.bulkEditModal.fields.placeholder')}
                            />
                        </div>
                    </div>

                    {/* Location e Max Partecipanti */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <MapPin className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.bulkEditModal.fields.newLocationLabel')}
                            </label>
                            <input
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                placeholder={t('gym.courses.bulkEditModal.fields.placeholder')}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <Users className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.bulkEditModal.fields.newMaxParticipantsLabel')}
                            </label>
                            <input
                                type="number"
                                name="max_participants"
                                value={formData.max_participants}
                                onChange={handleChange}
                                placeholder={t('gym.courses.bulkEditModal.fields.placeholder')}
                                min="1"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>

                    {/* Note */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                            {t('gym.courses.bulkEditModal.fields.newNotesLabel')}
                        </label>
                        <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            rows="3"
                            placeholder={t('gym.courses.bulkEditModal.fields.placeholder')}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 dark:text-white/85 hover:bg-gray-100 dark:hover:brightness-110 rounded-lg"
                        >
                            {t('gym.courses.bulkEditModal.actions.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
                        >
                            {loading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                                <Save size={16} />
                            )}
                            {t('gym.courses.bulkEditModal.actions.update', { count: selectedRecurrences.length })}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
